'use client';

import { AlertTriangle, RotateCw } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../atoms/button/button';

export interface RouteErrorScreenProps {
	/** Titolo. Default: "Qualcosa è andato storto". */
	title?: React.ReactNode;
	/** Messaggio esplicativo. Default: messaggio generico sull'errore di caricamento. */
	message?: React.ReactNode;
	/** Dettaglio tecnico (status HTTP o messaggio dell'errore); se assente il blocco non viene mostrato. */
	details?: string | null;
	/** Etichetta del pulsante primario. Default: "Ricarica la pagina". */
	retryLabel?: React.ReactNode;
	/** Ricarica la pagina / riprova; se assente il pulsante non viene mostrato. */
	onRetry?: () => void;
	/** Etichetta del pulsante secondario. Default: "Torna alla Dashboard". */
	homeLabel?: React.ReactNode;
	/** Torna alla dashboard; se assente il pulsante non viene mostrato. */
	onGoHome?: () => void;
}

/**
 * Schermata mostrata quando una rotta solleva un errore non gestito (`errorElement`
 * del router): sostituisce la pagina di default di React Router con un messaggio
 * comprensibile, il dettaglio tecnico e le azioni di recupero.
 */
const RouteErrorScreen = ({
	title = 'Qualcosa è andato storto',
	message = 'Non è stato possibile caricare questa pagina. Riprova: se il problema persiste, contatta il supporto.',
	details,
	retryLabel = 'Ricarica la pagina',
	onRetry,
	homeLabel = 'Torna alla Dashboard',
	onGoHome,
}: RouteErrorScreenProps) => (
	<div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
		<AlertTriangle className="h-12 w-12 text-danger" />
		<div>
			<div className="text-lg font-bold text-text-primary">{title}</div>
			<div className="mt-1 max-w-md text-sm text-text-muted">{message}</div>
		</div>
		{details && (
			<pre className="max-w-full overflow-x-auto rounded-lg border border-border bg-card px-3 py-2 text-left font-mono text-[11px] text-text-muted">
				{details}
			</pre>
		)}
		<div className="flex flex-wrap items-center justify-center gap-2">
			{onRetry && (
				<Button variant="accent" size="sm" onClick={onRetry}>
					<RotateCw />
					{retryLabel}
				</Button>
			)}
			{onGoHome && (
				<Button variant="outline" size="sm" onClick={onGoHome}>
					{homeLabel}
				</Button>
			)}
		</div>
	</div>
);
RouteErrorScreen.displayName = 'RouteErrorScreen';

export { RouteErrorScreen };
